import * as Yup from 'yup';
import { isEmpty } from 'lodash-es';
import { DateMode } from '@service-victoria/component-library/components/Date';
import { isDDMMYYYY, isMMYYYY } from './date';
import { constants, date as dateUtils } from 'utils';
import { LastDayOfPermits, LastDayOfPermitsText } from './constants';

const minDate = dateUtils.add(dateUtils.NowAsString('FullDate'), -120, 'y', 'FullDate');

export const date = {
  /**
   * Generate a schema for validating a date of birth field
   * @param mode format of the given date ('Full' or 'MonthYear')
   * @param extraAllValidation append extra validations to the date `all` field once all generic fields have been validated successfully
   */
  dateOfBirth: (mode: DateMode) => {
    return Yup.mixed()
      .test(
        'isValidDate',
        constants.DateValidationErrors.invalidDate,
        function validate(value: string) {
          return !value
            ? false
            : mode === 'FullDate'
            ? isDDMMYYYY(value, 'YYYY-MM-DD')
            : isMMYYYY(value);
        }
      )
      .test(
        'minimum date',
        'Sorry, you must be over 15 years old to continue.',
        function validate(value: string) {
          // Don't validate the maximum if not full date was entered
          if (!value) {
            return false;
          }
          const maxDate = dateUtils.add(dateUtils.NowAsString('FullDate'), -15, 'y', 'FullDate');
          return dateUtils.compare(value, maxDate, 'FullDate') <= 0;
        }
      )
      .test(
        'maximum date',
        `That doesn’t look right. Please enter a year after ${minDate.split('-')[0]}`,
        function validate(value: string) {
          if (!value) {
            return false;
          }

          return !!value && dateUtils.compare(value, minDate, 'FullDate') >= 0;
        }
      );
  },

  dateOfEvent: (
    mode: DateMode,
    isInternationalArrivalPermit: boolean,
    lastDate = LastDayOfPermits,
    lastDateText = LastDayOfPermitsText
  ) => {
    return Yup.string()
      .test(
        'required',
        constants.GenericValidationErrors.required,
        function validate(value?: string) {
          return !!value;
        }
      )
      .test(
        'isValidDate',
        constants.DateValidationErrors.invalidDate,
        function validate(value: string) {
          return !value
            ? false
            : mode === 'FullDate'
            ? isDDMMYYYY(value, 'YYYY-MM-DD')
            : isMMYYYY(value);
        }
      )
      .test(
        'date is before today',
        `The date can't be before today or after ${lastDateText}`,
        function validate(value: string) {
          // Don't validate the maximum if not full date was entered
          if (!value) {
            return false;
          }
          const now = dateUtils.NowAsString('FullDate');
          return (
            dateUtils.compare(value, now, 'FullDate') >= 0 &&
            dateUtils.compare(value, lastDate, 'FullDate') <= 0
          );
        }
      );
  },

  /**
   * Date validatiors functions for any part of the date
   */
  sections: {
    day: (value: string) => {
      return !isEmpty(value) && value.length === 2 && /(0[1-9]|[12]\d|3[01])/.test(value);
    },
    month: (value: string) => {
      return !isEmpty(value) && value.length === 2 && /(0[1-9]|1[012])/.test(value);
    },
    year: (value: string) => {
      return !isEmpty(value) && value.length === 4;
    },
    all: (mode: DateMode, value: string) => {
      return mode === 'FullDate' ? isDDMMYYYY(value) : isMMYYYY(value);
    }
  }
};

export const generic = {
  isNumber: (val?: unknown) => {
    // empty string is parsed as valid so need to check this edge case
    return val !== '' && !isNaN(Number(val));
  }
};

/**
 * merge multiple schemas together
 * @param schemas collection of schemas
 */
export function mergeSchemas(...schemas: Yup.Schema<unknown>[]) {
  const [first, ...rest] = schemas;

  const merged = rest.reduce((mergedSchemas, schema) => mergedSchemas.concat(schema), first);

  return merged;
}

// Inspired from: https://github.com/mathiasbynens/emoji-regex/blob/main/src/index.js
export const doesNotContainEmoji = (text?: string) => {
  return !text?.match(/\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Emoji_Modifier_Base}/gu);
};

/**
 *  Checks if the string contains the passed array
 */
export const checkStringContains = (string: string, contains: string[]) => {
  let matched = false;

  contains.forEach(item => {
    const cleanString = string.toLowerCase().replace(/[^\w\s]/gi, '');
    if (cleanString.indexOf(item) >= 0) {
      matched = true;
    }
  });

  return matched;
};
